import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SecureImage } from "@/components/SecureImage";
import { FileUploadField } from "@/components/FileUploadField";
import { Loader2, ChevronLeft, QrCode, CheckCircle2, Clock, CreditCard } from "lucide-react";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { motion } from "framer-motion";

const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-2 border-b border-border/60 last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`text-sm font-semibold ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
);

export default function Repay() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const loanId = sp.get("loan");
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ amount: "", utr: "", payment_date: "", screenshot_file_id: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [onlineEnabled, setOnlineEnabled] = useState(false);
  const [payingOnline, setPayingOnline] = useState(false);

  const load = async () => {
    try {
      if (loanId) {
        let loan, dash;
        try {
          [{ data: loan }, { data: dash }] = await Promise.all([
            api.get(`/loans/${loanId}`),
            api.get("/dashboard"),
          ]);
        } catch {
          toast.error("This loan could not be found"); nav("/"); return;
        }
        if (loan.status === "disbursed") {
          setOffer(loan);
          setForm((f) => ({ ...f, amount: String(loan.outstanding) }));
        } else { toast.error("This loan is not open for repayment"); nav("/"); return; }
        setSettings(dash.repayment_settings);
      } else {
        const { data } = await api.get("/dashboard");
        if (data.offer && data.offer.status === "disbursed") {
          setOffer(data.offer);
          setForm((f) => ({ ...f, amount: String(data.offer.outstanding) }));
        } else { toast.error("No active loan to repay"); nav("/"); return; }
        setSettings(data.repayment_settings);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); api.get("/repay/online/config").then(({ data }) => setOnlineEnabled(!!data.enabled)).catch(() => {}); }, []);

  const payOnline = async () => {
    setPayingOnline(true);
    try {
      const { data } = await api.post("/repay/online/create-order", loanId ? { offer_id: loanId } : {});
      window.location.assign(data.cashier_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start online payment");
    } finally { setPayingOnline(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.utr.trim() || !form.payment_date || !form.screenshot_file_id) {
      toast.error("Please fill amount, UTR, date and upload the payment screenshot");
      return;
    }
    setBusy(true);
    try {
      await api.post("/repayments", { ...form, amount: parseFloat(form.amount), offer_id: loanId || undefined });
      setSubmitted(true);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  return (
    <div className="min-h-screen bg-background pb-16">
      <header className="sticky top-0 z-40 h-16 flex items-center justify-between px-4 sm:px-6 border-b border-border bg-white/90 backdrop-blur-md">
        <Logo />
        <button onClick={() => nav("/")} className="text-sm text-muted-foreground hover:text-foreground flex items-center"><ChevronLeft className="h-4 w-4" /> Dashboard</button>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6">
        {submitted ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center pt-6" data-testid="repay-submitted">
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-amber-50 text-amber-600 mb-4"><Clock className="h-8 w-8" /></div>
            <h1 className="text-2xl font-extrabold tracking-tight">Payment submitted.</h1>
            <p className="text-muted-foreground mt-1">Waiting for repayment confirmation. Status: <b>Payment Verification Pending</b>.</p>
            <Button onClick={() => nav("/")} className="mt-6 h-11">Back to Dashboard</Button>
          </motion.div>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight">Repay your loan</h1>
            <p className="text-muted-foreground text-sm mt-1 mb-5">Pay using the QR below, then upload your payment details.</p>

            <Card className="p-5 sm:p-6 ip-card-shadow mb-5">
              <Row label="Outstanding amount" value={formatINR(offer.outstanding)} mono />
              <Row label="Total repayment" value={formatINR(offer.repayment_amount)} mono />
              <Row label="Amount paid" value={formatINR(offer.amount_paid)} mono />
            </Card>

            {settings?.enabled ? (
              <Card className="p-5 sm:p-6 ip-card-shadow mb-5" data-testid="repay-qr-card">
                <div className="flex items-center gap-2 text-brand mb-3"><QrCode className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Scan to Pay</span></div>
                {settings.qr_file_id ? (
                  <div className="flex justify-center">
                    <SecureImage fileId={settings.qr_file_id} alt="Payment QR" testId="repay-qr-image" className="h-56 w-56 object-contain rounded-lg border border-border bg-white" />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">QR code not available yet. Please check back shortly.</p>
                )}
                {settings.upi_id && <p className="text-center font-mono text-sm mt-3">{settings.upi_id}</p>}
                {settings.instructions && <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line">{settings.instructions}</p>}
              </Card>
            ) : (
              <Card className="p-5 ip-card-shadow mb-5"><p className="text-sm text-muted-foreground">Online repayment is temporarily unavailable. Please contact support.</p></Card>
            )}

            {onlineEnabled && (
              <Card className="p-5 sm:p-6 ip-card-shadow mb-5" data-testid="online-repay-card">
                <div className="flex items-center gap-2 text-brand mb-1"><CreditCard className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Pay Online</span></div>
                <p className="text-sm text-muted-foreground">Pay your outstanding securely via BladePay — your loan ledger updates automatically once the payment is verified.</p>
                <div className="mt-3 flex items-end justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Amount due now</p>
                    <p className="font-mono text-2xl font-extrabold text-brand" data-testid="online-repay-amount">{formatINR(offer.outstanding)}</p>
                  </div>
                  <Button onClick={payOnline} disabled={payingOnline || offer.outstanding <= 0} className="h-11 bg-gold hover:bg-gold-dark text-white" data-testid="online-repay-button">
                    {payingOnline ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />} Pay Online
                  </Button>
                </div>
              </Card>
            )}

            <form onSubmit={submit}>
              <Card className="p-5 sm:p-6 ip-card-shadow space-y-4">
                <h2 className="font-semibold">Submit payment details</h2>
                <div><Label className="mb-1.5 block">Amount paid (₹)</Label><Input data-testid="repay-amount-input" inputMode="numeric" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">UTR / Transaction reference</Label><Input data-testid="repay-utr-input" value={form.utr} onChange={(e) => setForm({ ...form, utr: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Payment date</Label><Input type="date" data-testid="repay-date-input" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Payment screenshot</Label>
                  <FileUploadField label="Upload screenshot" purpose="payment_screenshot" accept="image/*" testId="repay-screenshot-upload" onUploaded={(id) => setForm((f) => ({ ...f, screenshot_file_id: id }))} />
                </div>
                <Button type="submit" disabled={busy || !settings?.enabled} className="w-full h-11" data-testid="repay-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Submit Payment
                </Button>
              </Card>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
